import { create } from 'zustand';
import { ToastType, ToastPlacement } from '../types/ui';

/**
 * Internal toast state used by the store. Kept minimal; routing/behavior is
 * decided downstream (portal vs inline) using `placement` and `adaptToModal`.
 */
interface Toast {
    message: string;
    type: ToastType;
    timeout?: number;
    placement?: ToastPlacement;
    /** Whether this toast should adapt to modal state changes */
    adaptToModal?: boolean;
    /** Progress percentage (0-100) for upload/progress toasts */
    progress?: number;
}

/** Centralized toast control surface consumed by UI layers. */
interface ToastStore {
    toast: Toast | null;
    showToast: (message: string, type: ToastType, timeoutOrOptions?: number | { timeout?: number; placement?: ToastPlacement; adaptToModal?: boolean; progress?: number }) => void;
    timeoutId: ReturnType<typeof setTimeout> | null;
    isModalOpen: boolean;
    exportInProgress: boolean;

    hideToast: () => void;
    /** Track modal open state for dynamic toast positioning */
    setModalOpen: (isOpen: boolean) => void;
    /** Set the export progress state */
    setExportInProgress: (inProgress: boolean) => void;
    /** Trigger export progress toast when modal closes during active export */
    triggerExportProgressToast: () => void;
    /** Clear export progress state when export completes */
    clearExportProgress: () => void;
}

/**
 * Maps legacy toast type strings to the current `ToastType` set. 
 * TODO : remove this down the line once we're solid on toast types
 */
const getValidToastType = (type: string): ToastType => {
    switch (type) {
        case 'success':
        case 'error':
        case 'warning':
        case 'info':
        case 'loading':
        case 'upload':
        case 'export_success':
        case 'export_bulk_success':
        case 'export_warning':
            return type as ToastType;
        default:
            return 'info';  
    }
};

/**
 * Centralized state and routing hints for the toast system.
 *
 * Why: Single-source of truth avoids overlapping timers and competing toasts.
 * 
 * How: replace-on-show, optional timeout with cleanup, and minimal routing
 * hints (`placement`, `adaptToModal`) consumed by `ToastPortal`/chat.
 *
 * Usage Patterns:
 * - Transient success: `useToastStore.getState().showToast('Saved', 'success', 1500)`
 * - Persistent progress: `useToastStore.getState().showToast('Processing…', 'loading')` then `hideToast()`
 * - Global export: `useToastStore.getState().showToast('Export done', 'export_success', { placement: 'bottom-center' })`
 *
 * @example Store usage
 * ```tsx
 * const { showToast, hideToast, toast } = useToastStore();
 * 
 * // Show a toast
 * showToast('Success!', 'success', 2000);
 * 
 * // Or with options
 * showToast('Processing...', 'loading', { placement: 'bottom-center', adaptToModal: true });
 * ```
 */
export const useToastStore = create<ToastStore>((set, get) => ({
    toast: null,
    timeoutId: null,
    isModalOpen: false,
    exportInProgress: false,
    
    showToast: (message, type, timeoutOrOptions) => {
        // Clear any existing timeout
        const { timeoutId } = get();
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // Normalize options
        const options = typeof timeoutOrOptions === 'number' 
            ? { timeout: timeoutOrOptions }
            : timeoutOrOptions || {};

        const validType = getValidToastType(type);
        const newToast: Toast = {
            message,
            type: validType,
            timeout: options.timeout,
            placement: options.placement,
            adaptToModal: options.adaptToModal,
            progress: options.progress,
        };

        // Set new toast
        set({ toast: newToast });

        // Auto-hide if timeout provided
        if (options.timeout && options.timeout > 0) {
            const newTimeoutId = setTimeout(() => {
                get().hideToast();
            }, options.timeout);
            set({ timeoutId: newTimeoutId });
        }
    },
    
    hideToast: () => {
        const { timeoutId } = get();
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        set({ toast: null, timeoutId: null });
    },
    
    setModalOpen: (isOpen) => {
        set({ isModalOpen: isOpen });
    },
    
    setExportInProgress: (inProgress) => {
        set({ exportInProgress: inProgress });
    },

    triggerExportProgressToast: () => {
        const { exportInProgress } = get();
        if (exportInProgress) {
            get().showToast('Export in progress...', 'export_warning', {
                placement: 'bottom-center',
                adaptToModal: true,
            });
        }
    },
    
    clearExportProgress: () => {
        set({ exportInProgress: false });
    },
}));



